import { Component, createEffect, createSignal, For, onMount, Show } from "solid-js";
import { createStore } from "solid-js/store";
import { fetchGet } from "../../api";
import QuillEditor, { IQuillEditorWrapper } from "../quill-editor";
import { Required } from "../required";
import { TextBox } from "../textbox";
import { RenderCollection } from "./render-collection";
import { CollectionResult, UserSession } from "../../types";
import { Modal, Button } from "solid-bootstrap";
import "./index.css";
import { fetchPost } from "../universal/Common";

type Props = {
    artworkId: string;
    showModal: () => boolean;
    onClose: () => void;
}
type State = {
    isContentReady: boolean;
    view: "add-to-new"|"add-to-existing";
}
type Model = {
    Name: string;
    IsPrivate: boolean;
    Description: string;
    DescriptionEditor: IQuillEditorWrapper;
}
export const AddToCollectionModal : Component<Props> = (props) => {
    const [state, setState] = createStore<State>({
        isContentReady: false,
        view: "add-to-existing"
    } as State);
    const [session, setSession] = createStore<UserSession>({} as UserSession);
    const loadSession = async () => {
        const res = await fetch("/Home/GetAuthenticatedUserJson");
        const json = await res.json();
        if (!json.Success) {
            return;
        }
        setSession(json.Data);
    }
    const [model, setModel] = createStore<Model>({
        Name: "",
        IsPrivate: false,
        Description: ""
    } as Model);
    const [collections, setCollections] = createStore<CollectionResult[]>([]);
    const ModelTextBox : Component<{ field: keyof Model }> = (props) =>
        <TextBox value={model[props.field] as string} onInput={(value) => setModel(props.field, value)} />;
    const loadCollections = async () => {
        const res = await fetchGet("/Artwork/GetCollectionsJson", { artworkId: props.artworkId });
        const json = await res.json();
        if (!json.Success) {
            return;
        }
        setCollections(json.Data);
    }
    const addToSelectedAlbum = async (item: CollectionResult) => {
        const res = await fetchGet("/Artwork/AddToCollection", { artworkId: props.artworkId, collectionId: item.Id });
        const json = await res.json();
        if (!json.Success) {
            return;
        }
        const i = collections.findIndex(x=> x.Id == item.Id);
        setCollections(i, "IsArtworkInCollection", true);
    };
    const removeFromSelectedAlbum = async (item: CollectionResult) => {
        const res = await fetchGet("/Artwork/RemoveFromCollection", { artworkId: props.artworkId, collectionId: item.Id });
        const json = await res.json();
        if (!json.Success) {
            return;
        }
        const i = collections.findIndex(x=> x.Id == item.Id);
        setCollections(i, "IsArtworkInCollection", false);
    };
    const createAlbumClicked = (e: Event) => {
        e.preventDefault();
        
        setState("view", "add-to-new");
    }
    const cancelClicked = (e: Event)=> {
        e.preventDefault();
        setState("view", "add-to-existing");
        if (!collections.length) {
            props.onClose();
        }
    };
    createEffect(() => {
        setVisible(props.showModal());
      });
    onMount(async () => {
        await loadSession();
        await loadCollections();
        if (collections.length == 0) {
            setState("view", "add-to-new");
        }
        setState("isContentReady", true);
        setVisible(props.showModal());
    });
    const onClose = () => {
        setState("view", "add-to-existing");
        props.onClose();
    }
    const createAlbumSubmitted = async (e: Event) => {
        e.preventDefault();
        const data = {
            ...model,
            Description: model.DescriptionEditor?.getHtml(),
            ArtworkId: props.artworkId
        };
        const res = await fetchPost("/Artwork/CreateAlbumJson", data);
        const json = await res.json();
        if (!json.Success) {
            return;
        }
        await loadCollections();
        setState("view", "add-to-existing");
    }
    const [visible, setVisible] = createSignal<boolean>(false);
    return (
        <>
            <Modal show={visible()}
                onHide={onClose}
                contentClassName="modal-content-1000" 
                dialogClassName="modal-dialog-1000"
                size="xl" animation={false}>
                <Modal.Header>
                    <Button variant="secondary" onClick={onClose} class="modal-button-close">Close</Button>
                    <Modal.Title class="modal-title">Add to your collection ...</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Show when={state.isContentReady}>
                        <Show when={state.view == "add-to-existing"}>
                            <div style="position: relative;">
                                <div style="margin-top: 10px;">
                                    <p>
                                        <b>Add the artwork to</b> or <b>remove it from</b> one of your collections ...
                                    </p>
                                    <div class="collections">
                                        <For each={collections}>{(item) =>
                                            <RenderCollection
                                                artworkId={props.artworkId}
                                                item={item}
                                                addToAlbum={addToSelectedAlbum}
                                                removeFromAlbum={removeFromSelectedAlbum}
                                            />
                                        }</For>
                                    </div>
                                </div>
                                <div style="margin-top: 20px;">
                                    <div class="row">
                                        <div class="col-md-6">
                                            <a class="btn btn-default" href="/MyARC/Collection/List">See your Collections page</a>
                                        </div>
                                        <div class="col-md-6" style="text-align: right;">
                                            <button class="btn btn-primary" type="button" onClick={createAlbumClicked}><span class="fa fa-plus-circle"></span> Create new collection</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Show>
                        <Show when={state.view == "add-to-new"}>
                            <div style="width: 410px; min-height: 500px;">
                                <h4 style="font-family: 'Oswald';">
                                    Create album
                                </h4>                
                                <form onSubmit={createAlbumSubmitted}>
                                    <div class="row">
                                        <div class="col-md-8">
                                            <label>
                                                <Required /> Title
                                            </label>
                                            <input class="form-control" value={model.Name} onInput={(e) => setModel("Name", e.currentTarget.value)} />
                                        </div>
                                        <div class="col-md-4">
                                            <div class="checkbox" style="padding-top: 20px;">
                                                <label>
                                                    <input checked={model.IsPrivate} type="checkbox" onInput={() => setModel("IsPrivate", !model.IsPrivate)} />
                                                        Private <span class="fa fa-unlock" classList={{ ["fa fa-unlock"]: !model.IsPrivate, ["fa fa-lock"]: model.IsPrivate }} ></span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="row" style="margin-top: 20px">
                                        <div class="col-md-12">
                                            <label>
                                                Description
                                            </label>
                                            <QuillEditor content={model.Description} setInstance={(editor) => setModel("DescriptionEditor", editor)} />
                                        </div>
                                    </div>
                                    <div class="row" style="margin-top: 20px; text-align: right;">
                                        <div class="col-md-12">
                                            <button class="btn btn-default" onClick={cancelClicked}>Back</button>
                                            <button class="btn btn-primary" type="submit">Create</button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </Show>            
                    </Show>
                </Modal.Body>
            </Modal>
        </>
    );
}